import type { ReactElement } from 'react';
import { useState } from 'react';
import { useUIDSeed } from 'react-uid';

import { toCurrency } from 'utils/numbers';
import { hasTouchScreen } from 'utils/device';
import useCurrentUser from 'hooks/useCurrentUser';

import Plan, { PlanType } from 'models/Plan';

import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import { Component as InfoIcon } from 'icons/icon-circle--info.svg';

import Modal from 'components/shared/Modal';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Dropdown from 'components/shared/Dropdown';
import AddExtrasDropdown from './AddExtrasDropdown';

type Props = {
  show: boolean;
  plan: Plan;
  setPlan: (plan: Plan) => void;
  onClose: () => void;
  onConfirm: () => void;
};

function AddExtrasModal({
  show,
  plan,
  setPlan,
  onClose,
  onConfirm,
}: Props): ReactElement {
  const seed = useUIDSeed();
  const { currentUser } = useCurrentUser();
  const { subscription } = currentUser;
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    extras,
    includedDinners,
    selectedExtras,
    selectedExtraList,
    maximumExtras,
    planPrice,
    extrasPrice,
    totalPrice,
  } = plan;

  const toggleTooltipVisibility = (value?) => {
    if (value === undefined) {
      setShowTooltip(!showTooltip);
    } else if (!hasTouchScreen) {
      setShowTooltip(value);
    }
  };

  const rollbackAndClose = () => {
    setPlan(new Plan(plan.name, subscription.extras));
    onClose();
  };

  return (
    <Modal
      show={show}
      onClose={rollbackAndClose}
      className={[
        'c-modal-add-extras',
        plan.type === PlanType.MEAL_KIT ? 'c-meal-kit-plan' : '',
      ].join(' ')}
      body={(
        <>
          <button
            className="close"
            type="button"
            aria-label="Close modal"
            onClick={rollbackAndClose}
          >
            <CloseIcon width="20" height="20" />
          </button>

          <h3
            className={[
              'c-modal-add-extras__title',
              'c-heading',
              'c-heading--brand-4',
            ].join(' ')}
          >
            Add an Extra!
          </h3>

          <p className="c-modal-add-extras__text u-text--sm">
            There’s nothing better than more plant-based meals.&nbsp;
            {plan.type === PlanType.MEAL_KIT
              ? 'Add a breakfast, lunch, or dinner to your box every week.'
              : 'Add additional Ready-to-Eat meals to your box each week!'}
          </p>

          {plan.type === PlanType.MEAL_KIT ? (
            <div className="c-modal-add-extras__extras row">
              {[...Array(maximumExtras)].map((el, i) => (
                <AddExtrasDropdown
                  plan={plan}
                  setPlan={setPlan}
                  selected={selectedExtraList[i] || null}
                  key={seed(i)}
                />
              ))}
            </div>
          ) : (
            <div className="c-modal-add-extras__dinners row">
              <div
                className={[
                  'col-12',
                  'col-md-4',
                  'align-items-center',
                  'justify-content-center',
                  'd-inline-flex',
                ].join(' ')}
              >
                <div className="subscription_extra_dinners">
                  <Dropdown
                    selectedValue={
                      selectedExtras.dinner + includedDinners
                    }
                    values={Array.from(
                      { length: maximumExtras + 1 },
                      (cur, i) => includedDinners + i,
                    )}
                    onSelect={(val) => {
                      const newPlan = new Plan(plan.name);
                      newPlan.selectedExtras.dinner = (
                        (val as number) - includedDinners
                      );
                      setPlan(newPlan);
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {plan.type === PlanType.MEAL_KIT && (
            <p className="c-modal-add-extras__subtext u-text--xs">
              <sup>*</sup>
              &nbsp;
              Breakfast, lunches, or dinners may not be aligned
              with any specific plan, or may not be gluten-free.
            </p>
          )}

          <br />

          <h3 className="c-modal-add-extras__summary-heading c-heading c-heading--brand-6">
            Summary
          </h3>

          <div className="c-modal-add-extras__summary ">
            <div>
              <div className="u-text u-text--sm">
                <strong>
                  {plan.title()}
                </strong>
                &nbsp;(
                {plan.type === PlanType.MEAL_KIT
                  ? includedDinners
                  : includedDinners + selectedExtras.dinner}
                x Dinners)
              </div>
              <div className="u-text u-text--sm">
                {toCurrency(planPrice)}
              </div>
            </div>

            {plan.type === PlanType.MEAL_KIT && (
              <div>
                <div className="u-text u-text--sm">
                  <strong>Extras</strong>
                  {` (${extras}x Meal${extras === 1 ? '' : 's'})`}
                </div>
                <div className="u-text u-text--sm">
                  {toCurrency(extrasPrice)}
                </div>
              </div>
            )}

            <div>
              <div className="u-text u-text--sm">
                <strong>Sales Tax</strong>
                &nbsp;
                <button
                  name="button"
                  type="button"
                  className="c-button c-button--popover"
                  aria-expanded="false"
                  aria-label="How taxes are calculated info"
                  onTouchEnd={() => toggleTooltipVisibility()}
                  onPointerEnter={() => toggleTooltipVisibility(true)}
                  onPointerLeave={() => toggleTooltipVisibility(false)}
                >
                  <OverlayTrigger
                    show={showTooltip}
                    overlay={(
                      <Tooltip id="tooltip">
                        <>
                          Taxes are calculated for each shipping address total
                          based on the state to which the item(s) is shipped.
                          We will add all applicable taxes to items shipped to
                          AL, AR, AZ, CA, CO, CT, DC, FL, GA, HI, IA, ID, IL,
                          IN, KS, KY, LA, MA, MD, ME, MI, MN, MO, MS, NC, ND,
                          NE, NJ, NM, NV, NY, OH, OK, PA, RI, SC, SD, TX, UT,
                          VA, VT, WA, WI, WV, and WY.
                        </>
                      </Tooltip>
                    )}
                  >
                    <InfoIcon height="14" width="14" />
                  </OverlayTrigger>
                </button>
              </div>
              <div className="u-text u-text--sm">TBD</div>
            </div>

            <div>
              <div className="u-text u-text--sm">
                <strong>Total</strong>
              </div>
              <div className="u-text u-text--sm">
                {toCurrency(totalPrice)}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center c-modal-add-extras__summary-action-btn">
            <button
              type="button"
              className="c-button c-button--small"
              onClick={rollbackAndClose}
            >
              Cancel
            </button>
            <button
              name="button"
              type="submit"
              className="c-button c-button--small c-button--secondary"
              onClick={onConfirm}
            >
              Save
            </button>
          </div>
        </>
      )}
    />
  );
}

export default AddExtrasModal;
